/**
 * "Which sites stay live?" - a checkbox list bounded by the quota ceiling.
 *
 * Shared by the two flows where an account falls over its site quota: a plan change
 * (`QuotaResolution`) and an add-on removal (`AddOnChangeDialog`). The ceiling is hard -
 * once the selection is full the remaining rows lock, so the server never receives an
 * invalid choice.
 */
import { Globe } from "lucide-react";
import { cn } from "../../lib/utils";
import { Checkbox } from "../../components/checkbox";
import { fill, type T } from "../i18n";
import type { QuotaOptionSite } from "../types";

export function SitePicker({
  sites,
  selected,
  capacity,
  onToggle,
  t,
  disabled,
}: {
  sites: QuotaOptionSite[];
  selected: string[];
  /** How many sites may be selected (the quota). */
  capacity: number;
  onToggle: (siteId: string) => void;
  t: T;
  disabled?: boolean;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{t("quota.pickTitle")}</p>
        <span
          className={cn(
            "text-xs tabular-nums",
            selected.length === 0 ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground",
          )}
        >
          {fill(t("quota.pickCounter"), { n: selected.length, max: capacity })}
        </span>
      </div>
      <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto">
        {sites.map((site) => {
          const checked = selected.includes(site.id);
          // Ceiling reached -> lock the unselected rows so the choice can't exceed the quota.
          const locked = !checked && selected.length >= capacity;
          return (
            <li key={site.id}>
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-2.5 rounded-md border p-2 transition-colors",
                  checked ? "border-primary/50 bg-primary/5" : "border-transparent hover:bg-muted/50",
                  (locked || disabled) && "cursor-not-allowed opacity-50",
                )}
              >
                <Checkbox checked={checked} disabled={locked || disabled} onCheckedChange={() => onToggle(site.id)} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground">{site.name}</span>
                  {site.host && (
                    <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                      <Globe className="size-3 shrink-0" />
                      {site.host}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">{t("quota.suspendNote")}</p>
    </div>
  );
}
